/**
 * ContractIntelligencePanel — Milo Contract Intelligence flow (governed, human-approved).
 *
 * Lifecycle (mirrors the backend exactly): analyze → review → propose → diff → apply → undo.
 *   • analyze  POST /api/vendors/contract/analyze            (read-only; stores a draft)
 *   • propose  POST …/extractions/{id}/propose-changeset     (pure compute; the diff)
 *   • apply    POST …/changesets/{id}/apply {confirm,hash}   (the ONLY write; gated)
 *   • undo     POST …/changesets/{id}/undo                   (full reversal)
 *
 * Nothing is written until the host approves the diff and presses Apply. The apply is
 * off by default (the "Milo actions" kill switch) — a 403 routes to an enable hint.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Check, AlertTriangle, Clock, FileText, Undo2, DollarSign, Calendar, Building2, Sparkles, Pencil } from 'lucide-react';
import C from '../../data/colors';
import { adminAuthHeader } from '../../data/api';
import { formatMoney } from '../../lib/vendorInsights';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Evaluated PER-REQUEST so the real admin token (set at login) is read, not a stale module-load value.
const ADMIN_AUTH = () => adminAuthHeader();
const GOLD = C.accentPrimary;

const MONEY_FIELDS = new Set(['contract_total', 'deposit_amount', 'balance_amount', 'paid_to_date', 'per_head_rate']);
const ANALYZE_STEPS = [
  'Reading the document',
  'Classifying the contract',
  'Extracting terms & obligations',
  'Scoring confidence & evidence',
  'Cross-checking against your event',
];

const confColor = (c) => (c === 'high' ? C.statusSuccess : c === 'medium' ? C.statusWarning : C.statusError);
const confLabel = (c) => (c === 'high' ? 'High' : c === 'medium' ? 'Medium' : c === 'low' ? 'Needs you' : '');

function rawVal(f) {
  if (!f || typeof f !== 'object') return null;
  return f.value_text != null ? f.value_text : (f.value_number != null ? f.value_number : null);
}
function fieldDisplay(f, money) {
  if (!f || typeof f !== 'object') return null;
  if (money && f.value_number != null) return formatMoney(f.value_number);
  const v = rawVal(f);
  return v == null ? null : String(v);
}
function changeDisplay(field, val) {
  if (val == null || val === '') return '—';
  if (MONEY_FIELDS.has(field) && typeof val === 'number') return formatMoney(val);
  return String(val);
}

// ── Inline editing ────────────────────────────────────────────────────────────
// Maps the FE field key to the extraction key + the money flag. The KEY is what we
// PATCH back onto the stored extraction draft so the governed propose→apply pipeline
// (which reads the stored extraction, not the request body) operates on corrected data.
const EDITABLE_FIELDS = [
  { key: 'company_name', money: false },
  { key: 'role_category', money: false },
  { key: 'contact_name', money: false },
  { key: 'phone', money: false },
  { key: 'email', money: false },
  { key: 'address', money: false },
  { key: 'contract_total', money: true },
  { key: 'deposit_amount', money: true },
  { key: 'balance_amount', money: true },
  { key: 'per_head_rate', money: true },
  { key: 'amount_paid', money: true },
  { key: 'invoice_number', money: false },
  { key: 'deposit_due_date', money: false },
  { key: 'balance_due_date', money: false },
  { key: 'guest_minimum', money: true },
  { key: 'final_count_date', money: false },
  { key: 'cancellation_deadline', money: false },
  { key: 'coverage_summary', money: false },
  { key: 'coverage_expiry', money: false },
  { key: 'guest_count', money: true },
];
const EDITABLE_MONEY = new Set(EDITABLE_FIELDS.filter((f) => f.money).map((f) => f.key));

// The raw string a host edits (what they see in the input), independent of money formatting.
function editableRaw(f) {
  const v = rawVal(f);
  return v == null ? '' : String(v);
}

// Re-shape an edited string back into the extraction field object the backend expects.
// Preserves confidence/evidence; promotes a host-edited value to 'high' so the
// confidence gate in _build_contract_changes never silently drops a corrected field.
function applyEditToField(prevField, raw, money) {
  const base = (prevField && typeof prevField === 'object') ? { ...prevField } : {};
  const trimmed = raw == null ? '' : String(raw).trim();
  if (trimmed === '') {
    base.value_text = null;
    base.value_number = null;
  } else if (money) {
    const num = Number(String(trimmed).replace(/[$,\s]/g, ''));
    if (Number.isFinite(num)) { base.value_number = num; base.value_text = null; }
    else { base.value_text = trimmed; base.value_number = null; }
  } else {
    base.value_text = trimmed;
    base.value_number = null;
  }
  base.confidence = 'high';            // host-confirmed → never dropped by the gate
  base.edited_by_host = true;          // breadcrumb for the apply log / audit
  return base;
}

// Build the corrected extraction object from the original + the host's edits.
function mergeEdits(extraction, edits) {
  const out = { ...(extraction || {}) };
  for (const { key, money } of EDITABLE_FIELDS) {
    if (!(key in edits)) continue;
    out[key] = applyEditToField(out[key], edits[key], money);
  }
  return out;
}

export default function ContractIntelligencePanel({ file, onClose, onApplied }) {
  const [stage, setStage] = useState('idle'); // idle|analyzing|review|diff|applied|error|not_configured|actions_disabled
  const [extraction, setExtraction] = useState(null);
  const [extractionId, setExtractionId] = useState(null);
  const [changeset, setChangeset] = useState(null);
  const [applied, setApplied] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const startedFor = useRef(null);
  // Host corrections, keyed by extraction field. Seeded lazily as the host opens each field
  // for edit. mergeEdits() folds these back into the extraction draft before propose.
  const [edits, setEdits] = useState({});
  const [editingKey, setEditingKey] = useState(null);   // which field is in edit mode (review)
  // True once we've persisted the host's edits to the stored extraction draft via the
  // (to-be-added) PATCH endpoint, so the governed propose/apply read the corrected data.
  const [editsPersisted, setEditsPersisted] = useState(false);
  // Set when the PATCH endpoint isn't available yet — surfaces a precise backend-needed banner.
  const [persistWarning, setPersistWarning] = useState('');

  // Kick off analyze exactly once per file.
  useEffect(() => {
    if (!file) { setStage('idle'); startedFor.current = null; return; }
    if (startedFor.current === file) return;
    startedFor.current = file;
    let cancelled = false;
    (async () => {
      setStage('analyzing'); setStep(0); setExtraction(null); setChangeset(null); setApplied(null); setErrMsg('');
      setEdits({}); setEditingKey(null); setEditsPersisted(false); setPersistWarning('');
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/api/vendors/contract/analyze`, fd, { headers: { ...ADMIN_AUTH() } });
        if (cancelled) return;
        if (res.data?.configured === false) { setStage('not_configured'); return; }
        if (!res.data?.ok || !res.data?.extraction_id) { setErrMsg(res.data?.message || 'Milo could not start reading this contract.'); setStage('error'); return; }
        const id = res.data.extraction_id;
        setExtractionId(id);
        // Async job: Milo reads the contract in the background (so a slow model can't trip the
        // gateway timeout). Poll the draft until it's ready — these GETs are fast and never time out.
        const startedAt = Date.now();
        const poll = async () => {
          if (cancelled) return;
          try {
            const g = await axios.get(`${API_URL}/api/vendors/contract/extractions/${id}`, { headers: ADMIN_AUTH() });
            if (cancelled) return;
            const st = g.data?.status;
            if (st === 'analyzing') {
              if (Date.now() - startedAt > 5 * 60 * 1000) {
                setErrMsg('This is taking longer than expected. Please try again, or upload a smaller / clearer file.'); setStage('error'); return;
              }
              setTimeout(poll, 2500); return;
            }
            if (st === 'error') { setErrMsg(g.data?.error || 'Milo could not read this contract.'); setStage('error'); return; }
            setExtraction(g.data?.extraction || {});
            setStage('review');
          } catch (e) {
            if (cancelled) return;
            setErrMsg(e?.response?.data?.detail || e.message || 'Could not check progress.'); setStage('error');
          }
        };
        poll();
      } catch (e) {
        if (cancelled) return;
        setErrMsg(e?.response?.data?.detail || e.message || 'Upload failed.');
        setStage('error');
      }
    })();
    return () => { cancelled = true; };
  }, [file]);

  // Cosmetic step ticker while analyzing.
  useEffect(() => {
    if (stage !== 'analyzing') return;
    const t = setInterval(() => setStep((s) => Math.min(s + 1, ANALYZE_STEPS.length - 1)), 520);
    return () => clearInterval(t);
  }, [stage]);

  // Persist host edits to the STORED extraction draft so the governed propose→apply pipeline
  // (which reads the stored extraction + a content hash over it, NOT this request body) operates
  // on the corrected values. Tries PATCH then PUT; if neither route exists yet, we surface a
  // precise backend-needed banner and let propose run on Milo's original read (never silently
  // applying an unsaved edit). Returns true when the corrected extraction is the source of truth.
  const persistEdits = useCallback(async (mergedExtraction) => {
    if (Object.keys(edits).length === 0) return true;   // nothing edited → original read is fine
    const url = `${API_URL}/api/vendors/contract/extractions/${extractionId}`;
    const payload = { extraction: mergedExtraction, edited_fields: Object.keys(edits) };
    try {
      try {
        await axios.patch(url, payload, { headers: ADMIN_AUTH() });
      } catch (e) {
        // Some gateways/route tables 404/405 an undefined PATCH; retry as PUT before giving up.
        if ([404, 405].includes(e?.response?.status)) {
          await axios.put(url, payload, { headers: ADMIN_AUTH() });
        } else { throw e; }
      }
      setExtraction(mergedExtraction);   // reflect the saved truth in the UI immediately
      setEditsPersisted(true);
      setPersistWarning('');
      return true;
    } catch (e) {
      if ([404, 405].includes(e?.response?.status)) {
        // Endpoint not deployed yet. FE fields are corrected + carried in the confirm payload,
        // but the WRITE still reads Milo's original draft — say so plainly, don't fake success.
        setPersistWarning(
          'Your corrections are shown here, but saving edits back to Milo needs a backend update ' +
          '(PATCH /api/vendors/contract/extractions/{id}). Until that ships, the applied values will ' +
          'use Milo\'s original read for any field you changed.');
        setEditsPersisted(false);
        return false;
      }
      throw e;   // real error (auth/network/etc.) → bubble to the error screen
    }
  }, [edits, extractionId]);

  const doPropose = useCallback(async () => {
    setBusy(true);
    try {
      // Fold host corrections into the extraction and persist them FIRST, so the diff Milo
      // computes — and the values apply later writes — reflect what the host actually approved.
      const merged = mergeEdits(extraction, edits);
      await persistEdits(merged);
      // Carry the corrected extraction + the edited field list in the body too. propose ignores
      // them today, but this future-proofs the wiring once the backend reads request-body edits.
      const res = await axios.post(
        `${API_URL}/api/vendors/contract/extractions/${extractionId}/propose-changeset`,
        { extraction: merged, edited_fields: Object.keys(edits) },
        { headers: ADMIN_AUTH() });
      setChangeset(res.data.changeset);
      setStage('diff');
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e.message); setStage('error');
    } finally { setBusy(false); }
  }, [extractionId, extraction, edits, persistEdits]);

  const doApply = useCallback(async () => {
    if (!changeset) return;
    setBusy(true);
    try {
      // The governed apply reads the STORED changeset + a hash over the stored extraction. We
      // also forward the host's corrected values + persistence flag so that, once the backend
      // honors body edits, no correction can be lost; today they ride along harmlessly.
      const res = await axios.post(`${API_URL}/api/vendors/contract/changesets/${changeset.id}/apply`,
        {
          confirm: true,
          content_hash: changeset.content_hash,
          edited_fields: Object.keys(edits),
          edits_persisted: editsPersisted,
          extraction: Object.keys(edits).length ? mergeEdits(extraction, edits) : undefined,
        }, { headers: ADMIN_AUTH() });
      setApplied(res.data);
      setStage('applied');
      if (onApplied) onApplied();
    } catch (e) {
      if (e?.response?.status === 403) setStage('actions_disabled');
      else { setErrMsg(e?.response?.data?.detail || e.message); setStage('error'); }
    } finally { setBusy(false); }
  }, [changeset, onApplied, edits, editsPersisted, extraction]);

  // `ex` (the extraction view-model) MUST be declared before the hooks below that close over it.
  // commitDiffEdit reads ex[field] and lists `ex` in its dependency array, which React evaluates
  // during render — so if `ex` were declared later, the dep array hits a temporal-dead-zone crash
  // ("Cannot access 'ex' before initialization") the moment the panel mounts.
  const ex = extraction || {};

  // Host corrected a value ON the confirmation/diff screen. Record it, re-persist the merged
  // extraction, and RE-PROPOSE so the changeset shown + the content hash that gates apply both
  // reflect the correction (the apply reads the stored changeset, so a stale diff must be rebuilt).
  const commitDiffEdit = useCallback(async (field, raw) => {
    setEditingKey(null);
    const prevRaw = editableRaw(ex[field]);
    if (raw === prevRaw && !(field in edits)) return;        // unchanged → no rebuild
    const nextEdits = { ...edits, [field]: raw };
    setEdits(nextEdits);
    setBusy(true);
    try {
      const merged = mergeEdits(extraction, nextEdits);
      await persistEdits(merged);
      const res = await axios.post(
        `${API_URL}/api/vendors/contract/extractions/${extractionId}/propose-changeset`,
        { extraction: merged, edited_fields: Object.keys(nextEdits) },
        { headers: ADMIN_AUTH() });
      setChangeset(res.data.changeset);
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e.message); setStage('error');
    } finally { setBusy(false); }
  }, [ex, edits, extraction, extractionId, persistEdits]);

  const doUndo = useCallback(async () => {
    const csId = applied?.changeset?.id;
    if (!csId) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/vendors/contract/changesets/${csId}/undo`, {}, { headers: ADMIN_AUTH() });
      if (onApplied) onApplied();
      if (onClose) onClose();
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e.message); setStage('error');
    } finally { setBusy(false); }
  }, [applied, onApplied, onClose]);

  if (!file || stage === 'idle') return null;

  const changes = changeset?.changes || [];
  const touched = new Set(changes.map((c) => c.field));
  const ripples = [];
  if (touched.has('contract_total') || touched.has('deposit_amount')) ripples.push('Finance & Dashboard totals update automatically');
  if (touched.has('vendor_status')) ripples.push('Event Readiness marks this vendor booked');
  if (touched.has('deposit_due_date') || touched.has('balance_due_date')) ripples.push('Payment reminders appear on your timeline');

  const setEdit = (key, raw) => setEdits((p) => ({ ...p, [key]: raw }));
  const isEdited = (key) => key in edits;

  // Rendered as a plain function call (NOT <Field/>) so typing doesn't remount the input and
  // drop focus. EVERY captured field is editable: a pencil opens an inline input; the value is
  // shown near-black + legible. Edited fields stay visible even if Milo read them blank, so the
  // host can FILL a missed value (e.g. the venue email Milo logged as the host's own).
  const renderField = (label, key, money) => {
    const f = edits[key] != null ? applyEditToField(ex[key], edits[key], money) : ex[key];
    const display = fieldDisplay(f, money);
    const editing = editingKey === key;
    // Show the box when Milo captured something OR the host is editing/has edited this field.
    if (display == null && !editing && !isEdited(key)) {
      // Offer an "Add" affordance for an empty field so any value can be corrected/filled.
      return (
        <div style={S.fieldBox} key={key}>
          <div style={S.fieldHead}><span style={S.fieldLabel}>{label}</span></div>
          <button type="button" style={S.addBtn} onClick={() => { setEdit(key, ''); setEditingKey(key); }}>
            <Pencil size={11} /> Add
          </button>
        </div>
      );
    }
    return (
      <div style={S.fieldBox} key={key}>
        <div style={S.fieldHead}>
          <span style={S.fieldLabel}>{label}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            {isEdited(key) && <span style={S.editedChip}>Edited</span>}
            {f?.confidence && !isEdited(key) && <span style={S.chip(confColor(f.confidence))}>{confLabel(f.confidence)}</span>}
            {!editing && (
              <button type="button" style={S.editIcon} title={`Edit ${label}`} onClick={() => { if (!isEdited(key)) setEdit(key, editableRaw(ex[key])); setEditingKey(key); }}>
                <Pencil size={12} />
              </button>
            )}
          </span>
        </div>
        {editing ? (
          <input
            autoFocus
            style={S.fieldInput}
            value={edits[key] != null ? edits[key] : editableRaw(ex[key])}
            placeholder={money ? '0.00' : `Enter ${label.toLowerCase()}`}
            inputMode={money ? 'decimal' : 'text'}
            onChange={(e) => setEdit(key, e.target.value)}
            onBlur={() => setEditingKey((k) => (k === key ? null : k))}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingKey(null); } }}
          />
        ) : (
          <div style={S.fieldVal} onClick={() => { if (!isEdited(key)) setEdit(key, editableRaw(ex[key])); setEditingKey(key); }}>
            {display == null ? <span style={{ color: C.textMuted, fontStyle: 'italic' }}>—</span> : display}
          </div>
        )}
        {f?.evidence_location && !editing && (
          <span style={S.evi} title={f.evidence_quote || ''}><FileText size={10} /> {f.evidence_location}</span>
        )}
      </div>
    );
  };

  return (
    <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose && onClose(); }}>
      <style>{KEYFRAMES}</style>
      <div style={S.modal}>
        {/* Header */}
        <div style={S.hdr}>
          <div style={S.orb}>M</div>
          <div style={{ flex: 1 }}>
            <div style={S.hTitle}>{HEADERS[stage]?.t || 'Milo Contract Intelligence'}</div>
            <div style={S.hSub}>{HEADERS[stage]?.s || (file?.name || '')}</div>
          </div>
          {!busy && <div style={S.x} onClick={onClose}><X size={18} /></div>}
        </div>

        {/* ───────── ANALYZING ───────── */}
        {stage === 'analyzing' && (
          <div style={S.bodyCenter}>
            <div style={S.bigOrb}>M<span style={S.ring} /></div>
            <div style={S.steps}>
              {ANALYZE_STEPS.map((label, i) => (
                <div key={i} style={{ ...S.stepRow, opacity: i <= step ? 1 : 0.4 }}>
                  <span style={{ ...S.stepDot, background: i <= step ? C.statusSuccessTint : '#EEE' }}>
                    {i <= step && <Check size={11} color={C.statusSuccess} strokeWidth={3} />}
                  </span>
                  {label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ───────── REVIEW ───────── */}
        {stage === 'review' && (
          <>
            <div style={S.body}>
              <div style={S.editHint}><Pencil size={12} color={GOLD} /> Tap any value to correct what Milo captured before it's applied.</div>
              <div style={S.sec}>Vendor</div>
              <div style={S.grid}>
                {renderField('Company', 'company_name', false)}
                {renderField('Category', 'role_category', false)}
                {renderField('Contact', 'contact_name', false)}
                {renderField('Phone', 'phone', false)}
                {renderField('Email', 'email', false)}
                {renderField('Address', 'address', false)}
              </div>
              <div style={S.sec}>Money</div>
              <div style={S.grid}>
                {renderField('Contract total', 'contract_total', true)}
                {renderField('Deposit', 'deposit_amount', true)}
                {renderField('Balance', 'balance_amount', true)}
                {renderField('Per-head rate', 'per_head_rate', true)}
                {renderField('Amount paid', 'amount_paid', true)}
                {renderField('Invoice #', 'invoice_number', false)}
              </div>
              <div style={S.sec}>Dates & Obligations</div>
              <div style={S.grid}>
                {renderField('Deposit due', 'deposit_due_date', false)}
                {renderField('Balance due', 'balance_due_date', false)}
                {renderField('Guest minimum', 'guest_minimum', true)}
                {renderField('Final count due', 'final_count_date', false)}
                {renderField('Cancellation by', 'cancellation_deadline', false)}
                {renderField('Insurance', 'coverage_summary', false)}
                {renderField('Coverage expires', 'coverage_expiry', false)}
                {renderField('Guests counted', 'guest_count', true)}
              </div>

              {(((ex.clarifying_questions || []).length > 0) || ((ex.risk_notes || []).length > 0)) && (
                <div style={S.miloRead}>
                  <div style={S.miloHead}><Sparkles size={14} color={GOLD} /> Milo's read</div>
                  {(ex.clarifying_questions || []).map((q, i) => (
                    <div key={`q${i}`} style={S.readRow}><AlertTriangle size={14} color={C.statusWarning} /><span>{q}</span></div>
                  ))}
                  {(ex.risk_notes || []).map((r, i) => (
                    <div key={`r${i}`} style={S.readRow}><AlertTriangle size={14} color={C.textMuted} /><span>{r} <em style={{ color: C.textMuted }}>(a possible issue to review — not legal advice)</em></span></div>
                  ))}
                </div>
              )}
              {persistWarning && <div style={S.warnBanner}><AlertTriangle size={13} color={C.statusWarning} /><span>{persistWarning}</span></div>}
              <div style={S.note}>Nothing is saved yet. Press below to see exactly what would change.</div>
            </div>
            <div style={S.foot}>
              <button style={S.btnGhost} onClick={onClose}>Discard</button>
              <button style={S.btnGold} disabled={busy} onClick={doPropose}>{busy ? 'Working…' : 'Looks right — show what changes'}</button>
            </div>
          </>
        )}

        {/* ───────── DIFF ───────── */}
        {stage === 'diff' && (
          <>
            <div style={S.body}>
              <div style={S.editHint}><Pencil size={12} color={GOLD} /> Still wrong? Tap a value below to fix it, or go Back to edit any captured field.</div>
              {changes.length === 0 && <div style={S.note}>Nothing new to apply — the vendor already matches this contract.</div>}
              {changes.map((c, i) => {
                const editing = editingKey === `diff:${c.field}`;
                const money = EDITABLE_MONEY.has(c.field);
                return (
                  <div key={i} style={S.diffRow}>
                    <div style={S.diffIcon}><Building2 size={15} color={GOLD} /></div>
                    <div style={{ flex: 1 }}>
                      <div style={S.diffLabel}>{c.label}</div>
                      {editing ? (
                        <input
                          autoFocus
                          style={{ ...S.fieldInput, marginTop: 4 }}
                          defaultValue={editableRaw({ value_text: typeof c.proposed_value === 'string' ? c.proposed_value : null, value_number: typeof c.proposed_value === 'number' ? c.proposed_value : null }) || (c.proposed_value != null ? String(c.proposed_value) : '')}
                          inputMode={money ? 'decimal' : 'text'}
                          onBlur={(e) => commitDiffEdit(c.field, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDiffEdit(c.field, e.target.value); } if (e.key === 'Escape') { e.preventDefault(); setEditingKey(null); } }}
                        />
                      ) : (
                        <div style={S.diffVals}>
                          <span style={S.old}>{changeDisplay(c.field, c.current_value)}</span>
                          <span style={{ color: C.textMuted }}>→</span>
                          <span style={S.new}>{changeDisplay(c.field, c.proposed_value)}</span>
                        </div>
                      )}
                    </div>
                    {isEdited(c.field) && !editing && <span style={S.editedChip}>Edited</span>}
                    {c.confidence && !isEdited(c.field) && !editing && <span style={S.chip(confColor(c.confidence))}>{confLabel(c.confidence)}</span>}
                    {!editing && (
                      <button type="button" style={S.editIcon} title={`Edit ${c.label}`} onClick={() => setEditingKey(`diff:${c.field}`)}><Pencil size={12} /></button>
                    )}
                  </div>
                );
              })}
              {ripples.length > 0 && (
                <div style={S.ripple}>
                  <div style={S.miloHead}><Sparkles size={14} color={GOLD} /> Milo will also</div>
                  {ripples.map((r, i) => (
                    <div key={i} style={S.readRow}><Check size={14} color={C.statusSuccess} /><span>{r}</span></div>
                  ))}
                </div>
              )}
              {persistWarning && <div style={S.warnBanner}><AlertTriangle size={13} color={C.statusWarning} /><span>{persistWarning}</span></div>}
            </div>
            <div style={S.foot}>
              <button style={S.btnGhost} onClick={() => { setEditingKey(null); setStage('review'); }}>Back</button>
              <button style={S.btnGold} disabled={busy || changes.length === 0} onClick={doApply}>{busy ? 'Applying…' : 'Apply approved changes'}</button>
            </div>
          </>
        )}

        {/* ───────── APPLIED ───────── */}
        {stage === 'applied' && (
          <>
            <div style={{ ...S.bodyCenter, gap: 14 }}>
              <div style={S.success}><Check size={34} color={C.statusSuccess} strokeWidth={2.5} /></div>
              <div style={S.doneTitle}>Done — applied across the platform</div>
              <div style={{ fontSize: 13, color: C.textSecondary, textAlign: 'center', maxWidth: 420 }}>
                {applied?.changeset?.mode === 'create' ? 'The vendor is live in your ledger.' : 'The vendor was updated.'} Finance, Calendar, and Event-Readiness updated automatically. Milo is watching the obligations from here.
              </div>
              {file?.name && (
                <div style={S.savedDoc} data-testid="contract-saved-doc">
                  <FileText size={14} color={C.statusSuccess} />
                  <span><b>{file.name}</b> is saved to this vendor — find it under the vendor's <b>Documents</b>.</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button style={S.btnGhost} onClick={doUndo} disabled={busy}><Undo2 size={14} /> {busy ? 'Undoing…' : 'Undo everything'}</button>
                <button style={S.btnGold} onClick={onClose}>Done</button>
              </div>
            </div>
          </>
        )}

        {/* ───────── NOT CONFIGURED ───────── */}
        {stage === 'not_configured' && (
          <div style={{ ...S.bodyCenter, gap: 12 }}>
            <Clock size={34} color={GOLD} />
            <div style={S.doneTitle}>Milo's AI brain isn't connected yet</div>
            <div style={{ fontSize: 13, color: C.textSecondary, textAlign: 'center', maxWidth: 440 }}>
              Add an <b>ANTHROPIC_API_KEY</b> to the backend environment to enable contract reading. The rest of the platform works normally — you can still add this vendor manually.
            </div>
            <button style={S.btnGold} onClick={onClose}>Got it</button>
          </div>
        )}

        {/* ───────── ACTIONS DISABLED (kill switch) ───────── */}
        {stage === 'actions_disabled' && (
          <div style={{ ...S.bodyCenter, gap: 12 }}>
            <AlertTriangle size={34} color={C.statusWarning} />
            <div style={S.doneTitle}>Milo actions are off</div>
            <div style={{ fontSize: 13, color: C.textSecondary, textAlign: 'center', maxWidth: 440 }}>
              Applying a contract writes across modules, so it's off by default. Turn on <b>Milo actions</b> in the AI tab's settings, then re-run this to apply. Your review is saved as a draft.
            </div>
            <button style={S.btnGold} onClick={onClose}>Close</button>
          </div>
        )}

        {/* ───────── ERROR ───────── */}
        {stage === 'error' && (
          <div style={{ ...S.bodyCenter, gap: 12 }}>
            <AlertTriangle size={34} color={C.statusError} />
            <div style={S.doneTitle}>Something went wrong</div>
            <div style={{ fontSize: 13, color: C.textSecondary, textAlign: 'center', maxWidth: 440 }}>{errMsg || 'Please try again.'}</div>
            <button style={S.btnGold} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

const HEADERS = {
  analyzing: { t: 'Milo is reading your contract…', s: 'Native PDF/image understanding' },
  review: { t: 'Here\'s what Milo extracted', s: 'Confidence + the exact clause. Nothing is saved yet.' },
  diff: { t: 'Everything that will change', s: 'Nothing writes until you press apply.' },
  applied: { t: 'Applied', s: 'Fully reversible' },
  not_configured: { t: 'Contract Intelligence', s: '' },
  actions_disabled: { t: 'Contract Intelligence', s: '' },
  error: { t: 'Contract Intelligence', s: '' },
};

const KEYFRAMES = `
@keyframes cip-spin { to { transform: rotate(360deg); } }
@keyframes cip-pulse { 0%,100%{ box-shadow:0 0 0 6px rgba(198,167,90,0.18);} 50%{ box-shadow:0 0 0 12px rgba(198,167,90,0.06);} }
`;

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(20,18,14,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal: { width: '100%', maxWidth: 760, maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: 20, border: `1px solid ${C.borderMedium}`, boxShadow: '0 40px 100px rgba(0,0,0,0.40)' },
  hdr: { display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px', borderBottom: `1px solid ${C.borderLight}` },
  orb: { width: 38, height: 38, borderRadius: '50%', background: `radial-gradient(circle at 35% 30%, #E6C579, ${GOLD})`, color: '#221b08', display: 'grid', placeItems: 'center', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 19, flex: 'none', boxShadow: '0 0 0 5px rgba(198,167,90,0.18)' },
  hTitle: { fontWeight: 700, fontSize: 16, color: C.textPrimary },
  hSub: { fontSize: 12, color: C.textMuted, marginTop: 1 },
  x: { width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center', color: C.textMuted, cursor: 'pointer', border: `1px solid ${C.borderLight}` },
  body: { padding: '18px 22px', overflowY: 'auto' },
  bodyCenter: { padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, overflowY: 'auto' },
  bigOrb: { position: 'relative', width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle at 35% 30%, #E6C579, ${GOLD})`, color: '#221b08', display: 'grid', placeItems: 'center', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 34, animation: 'cip-pulse 2.4s ease-in-out infinite' },
  ring: { position: 'absolute', inset: -9, borderRadius: '50%', border: `2px solid ${GOLD}`, borderTopColor: 'transparent', animation: 'cip-spin 1.1s linear infinite' },
  steps: { width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 9 },
  stepRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: C.textPrimary, transition: 'opacity .3s' },
  stepDot: { width: 20, height: 20, borderRadius: '50%', display: 'grid', placeItems: 'center', flex: 'none' },
  sec: { fontSize: 10, fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase', color: GOLD, margin: '14px 0 9px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 },
  fieldBox: { border: `1px solid ${C.borderLight}`, borderRadius: 11, padding: '9px 12px', background: '#FFFFFF' },
  fieldHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 },
  // #1 — label enlarged 9→10.5 and darkened (was textMuted) so the captured field is legible.
  fieldLabel: { fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: C.textSecondary },
  // #1 — captured VALUE is the thing the host reads: bigger (13.5→15.5), weightier, explicit near-black.
  fieldVal: { fontSize: 15.5, fontWeight: 700, lineHeight: 1.35, marginTop: 4, color: '#15120C', cursor: 'text', wordBreak: 'break-word' },
  // Inline edit input — matches the value's size/weight/color so corrected text stays just as legible.
  fieldInput: { width: '100%', boxSizing: 'border-box', marginTop: 4, fontSize: 15.5, fontWeight: 700, color: '#15120C', background: '#FFFDF7', border: `1.5px solid ${GOLD}`, borderRadius: 8, padding: '6px 9px', outline: 'none', fontFamily: 'inherit' },
  editIcon: { display: 'inline-grid', placeItems: 'center', width: 24, height: 24, borderRadius: 7, border: `1px solid ${C.borderMedium}`, background: '#FFFFFF', color: GOLD, cursor: 'pointer', flex: 'none' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 12, fontWeight: 700, color: GOLD, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' },
  editedChip: { fontSize: 8.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 99, background: C.accentTintMedium, color: '#8A6D1F' },
  editHint: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: C.textSecondary, background: C.accentTintLight, border: `1px solid ${C.borderMedium}`, borderRadius: 10, padding: '8px 11px', marginBottom: 4 },
  warnBanner: { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 11.5, lineHeight: 1.45, color: C.textSecondary, background: C.statusWarningTint, border: `1px solid ${C.statusWarning}55`, borderRadius: 10, padding: '9px 11px' },
  chip: (col) => ({ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 99, background: col + '22', color: col }),
  evi: { marginTop: 6, fontSize: 10, color: GOLD, display: 'inline-flex', alignItems: 'center', gap: 4, borderBottom: `1px dashed ${C.borderStrong}`, cursor: 'help' },
  miloRead: { marginTop: 16, background: C.accentTintLight, border: `1px solid ${C.borderMedium}`, borderRadius: 13, padding: 14 },
  miloHead: { fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, color: C.textPrimary },
  readRow: { display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12, color: C.textSecondary, padding: '5px 0', lineHeight: 1.5 },
  note: { marginTop: 14, fontSize: 11.5, color: C.textMuted, textAlign: 'center' },
  ripple: { marginTop: 14, background: C.accentTintLight, border: `1px solid ${C.borderMedium}`, borderRadius: 13, padding: 14 },
  diffRow: { display: 'flex', alignItems: 'center', gap: 13, padding: '13px 0', borderBottom: `1px solid ${C.borderLight}` },
  diffIcon: { width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center', background: C.accentTintLight, flex: 'none', boxShadow: `inset 0 0 0 1px ${C.borderMedium}` },
  diffLabel: { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textMuted },
  diffVals: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 13.5 },
  old: { color: C.textMuted, textDecoration: 'line-through' },
  new: { color: C.textPrimary, fontWeight: 700 },
  foot: { padding: '14px 22px', borderTop: `1px solid ${C.borderLight}`, display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#FCFBF8' },
  btnGold: { display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 12.5, border: 'none', cursor: 'pointer', borderRadius: 11, padding: '11px 16px', background: `linear-gradient(180deg, #E6C579, ${GOLD})`, color: '#221b08', boxShadow: '0 6px 18px rgba(198,167,90,0.32)' },
  btnGhost: { display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', borderRadius: 11, padding: '11px 16px', background: '#FFFFFF', border: `1px solid ${C.borderMedium}`, color: C.textPrimary },
  success: { width: 72, height: 72, borderRadius: '50%', background: C.statusSuccessTint, display: 'grid', placeItems: 'center' },
  doneTitle: { fontSize: 20, fontWeight: 700, color: C.textPrimary, fontFamily: "'Playfair Display', Georgia, serif", textAlign: 'center' },
  savedDoc: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, fontSize: 12.5, lineHeight: 1.45, color: C.textSecondary, background: C.statusSuccessTint, border: `1px solid ${C.statusSuccess}33`, borderRadius: 10, padding: '9px 13px', maxWidth: 420, textAlign: 'left' },
};
